import React, { FC, FormEvent } from 'react';
// import { useTranslation } from 'react-i18next';
import ReCAPTCHA from 'react-google-recaptcha';
import cx from 'clsx';

import { CheckoutPayResponse, Customer } from 'services/checkout-service/models';
import { getApplePayMerchantSession } from 'services/cos-service/apple-pay-session';
import { makeApplePayCToken } from 'services/tokenization-service/tokenization';
// import { useMerchantPaymentConfigContext } from 'hooks/context/merchantPaymentConfig';
import { useCheckoutCurrency } from 'hooks/context/checkoutSession';
import { doRecaptcha } from 'utils/Recaptcha';
import { makeApplePayRequestBody } from './utils';
import { WalletSubmitPaymentType } from '../index';
import { SetPaymentOverlayType } from '../../CloverPaymentSection';

import styles from './styles.module.scss';

// type ApplePayLineItem = ApplePayJS.ApplePayLineItem;
// type ApplePayPaymentMethodUpdate = ApplePayJS.ApplePayPaymentMethodUpdate;

const APPLE_PAY_VERSION = 3;
// const DEBIT = 'debit';

type ApplePayProps = {
  subtotal: number;
  onWalletSubmit: WalletSubmitPaymentType;
  setPaymentOverlays: SetPaymentOverlayType;
  isRecaptchaEnabled: boolean;
};

export const ApplePayButton: FC<ApplePayProps> = ({
  subtotal,
  onWalletSubmit,
  setPaymentOverlays,
  isRecaptchaEnabled,
}) => {
  const currency = useCheckoutCurrency();
  // const [t] = useTranslation();
  // const { merchantPaymentConfig } = useMerchantPaymentConfigContext();
  // const isSurchargeSupported = useMemo(() => merchantPaymentConfig?.surcharging?.supported ?? false, [merchantPaymentConfig]);
  // const surchargeRate = isSurchargeSupported ? merchantPaymentConfig?.surcharging.rate : undefined;
  // const surchargeAmount = surchargeRate ? surchargeRate * subtotal : undefined;

  const applePayButtonClicked = (
    e: FormEvent,
    payRequest: ApplePayJS.ApplePayPaymentRequest
  ) => {
    e.preventDefault();
    setPaymentOverlays(true);
    const session = new ApplePaySession(APPLE_PAY_VERSION, payRequest);

    session.oncancel = () => session.completePayment(ApplePaySession.STATUS_FAILURE);
    /**
     * Merchant Validation
     * We call our merchant session endpoint, passing the URL to use
     * URL should look something like - validationURL: "https://apple-pay-gateway-cert.apple.com/paymentservices/startSession"
     */
    session.onvalidatemerchant = ({ validationURL }: ApplePayJS.ApplePayValidateMerchantEvent) => {
      getApplePayMerchantSession(validationURL)
        .then(merchantSession => session.completeMerchantValidation(merchantSession))
        .catch(() => {
          session.completePayment(ApplePaySession.STATUS_FAILURE);
        });
    };

    // Keep this for future in case we need it for surcharging on client.
    // session.onpaymentmethodselected = (event: ApplePayJS.ApplePayPaymentMethodSelectedEvent) => {
    //   const { type } = event.paymentMethod;
    //   const existingLineItems = payRequest.lineItems;
    //
    //   // debit cards can't be surcharged but all other payment methods are surchargeable
    //   // so add line surcharge as line item and include it in the total
    //   if (type !== DEBIT && surchargeAmount && surchargeRate) {
    //     // update line items to be shown on the apple charge sheet
    //     const updatedLineItems: ApplePayLineItem[] = [{
    //       label: t('surcharge', { rate: (surchargeRate * 100).toFixed(2) }),
    //       amount: parseFloat((surchargeAmount / 100).toString()).toFixed(2),
    //     }];
    //
    //     const newTotal = subtotal + surchargeAmount;
    //     // eslint-disable-next-line no-console
    //     console.info(`Using surcharge: ${surchargeAmount} with subtotal: ${subtotal}`);
    //     const updatedTransactionDetailsWithSurcharge: ApplePayPaymentMethodUpdate = {
    //       newLineItems: [...(existingLineItems ?? []), ...updatedLineItems],
    //       newTotal: {
    //         label: t('total'),
    //         amount: parseFloat((newTotal / 100).toString()).toFixed(2),
    //       },
    //     };
    //
    //     // calling this method will update the charge sheet with surcharge line item and amount
    //     return session.completePaymentMethodSelection(updatedTransactionDetailsWithSurcharge);
    //   }
    //
    //   // eslint-disable-next-line no-console
    //   console.info(`Using non-surcharge with subtotal: ${subtotal}`);
    //   const existingTransactionalDetails: ApplePayPaymentMethodUpdate = {
    //     newLineItems: existingLineItems,
    //     newTotal: {
    //       label: t('total'),
    //       amount: parseFloat((subtotal / 100).toString()).toFixed(2),
    //     },
    //   };
    //
    //   session.completePaymentMethodSelection(existingTransactionalDetails);
    // };

    session.onpaymentauthorized = (event: ApplePayJS.ApplePayPaymentAuthorizedEvent) => {
      // eslint-disable-next-line no-console
      makeApplePayCToken(event)
        .then(async walletResponse => {
          const customerEmail = event.payment.shippingContact?.emailAddress;
          const billingContactDetails = event.payment.billingContact;

          if (!customerEmail) {
            console.error('Customer email is required');
            session.completePayment(ApplePaySession.STATUS_INVALID_SHIPPING_CONTACT);

            return;
          }

          let customer = {
            ...walletResponse.customer,
            email: customerEmail,
          };

          //consume customer details from billing address
          if (billingContactDetails) {
            const {
              givenName = '',
              familyName = '',
              addressLines,
              locality = '',
              administrativeArea = '',
              postalCode = '',
              countryCode = '',
            } = billingContactDetails;

            customer = {
              ...customer,
              firstName: givenName,
              lastName: familyName,
              address: {
                elements: [
                  {
                    address1: addressLines?.[0] || '',
                    address2: addressLines?.[1] || '',
                    city: locality,
                    state: administrativeArea,
                    zip: postalCode,
                    country: countryCode,
                  },
                ],
              },
            };
          }

          const wallet = {
            token: walletResponse.token,
            customer,
          };

          let recaptchaToken = undefined;
          if (isRecaptchaEnabled) {
            // TODO: Show a error on recaptcha API failure.
            // @ts-ignore
            recaptchaToken = await doRecaptcha(window["recaptchaRef"], undefined);
            if (!recaptchaToken) {
              console.error("Failed to generate recaptcha token");
              return;
            }
          }


          onWalletSubmit(wallet, (payResponse?: CheckoutPayResponse) => {
            // Submit payment gracefully handles payment call errors
            // but apple pay needs to be notified so check if data is defined
            if (payResponse) {
              session.completePayment(ApplePaySession.STATUS_SUCCESS);
            } else {
              session.completePayment(ApplePaySession.STATUS_FAILURE);
            }
          }, 'APPLE_PAY', recaptchaToken || '');
        }).catch(() => {
          session.completePayment(ApplePaySession.STATUS_FAILURE);
      });
    };

    // All our handlers are setup - start the Apple Pay payment
    session.begin();
  };

  const payRequest = makeApplePayRequestBody(subtotal, currency);

  return (
    <button
      className={cx(styles.applePayButton, styles.applePayButtonBlack, styles.payButton)}
      onClick={(event) => applePayButtonClicked(event, payRequest)}
    />
  );
};
